import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Info, Power } from 'lucide-react';
import type { ChestContextConfig, ChestContextKey, EquipmentCatalogItem } from './chestConfigTypes';
import { getItemsForContext, MERCH_GEO_ADMIN_NOTE, PRO_1M_ADMIN_NOTE } from './chestConfigTypes';
import {
    groupItemsByCategory,
    sumEnabledWeights,
    toggleContextEnabled,
    toggleItemEnabled,
    updateItemWeight,
} from './chestConfigUtils';

interface ChestContextEditorProps {
    contextKey: ChestContextKey;
    config: ChestContextConfig;
    equipmentCatalog: EquipmentCatalogItem[];
    onChange: (next: ChestContextConfig) => void;
    percentMode: boolean;
}

const GROUP_COLORS: Record<string, string> = {
    'Suscripción Pro': 'bg-amber-500',
    Merchandising: 'bg-fuchsia-500',
    Equipamiento: 'bg-violet-500',
    'Comodines Quiz': 'bg-blue-500',
    'Objetos de captura': 'bg-emerald-500',
    'Objetos de batalla': 'bg-orange-500',
    'Tipo de encuentro': 'bg-cyan-500',
};

const GROUP_BORDER: Record<string, string> = {
    'Suscripción Pro': 'border-amber-500/20 bg-amber-500/5',
    Merchandising: 'border-fuchsia-500/20 bg-fuchsia-500/5',
    Equipamiento: 'border-violet-500/20 bg-violet-500/5',
    'Comodines Quiz': 'border-blue-500/20 bg-blue-500/5',
    'Objetos de captura': 'border-emerald-500/20 bg-emerald-500/5',
    'Objetos de batalla': 'border-orange-500/20 bg-orange-500/5',
    'Tipo de encuentro': 'border-cyan-500/20 bg-cyan-500/5',
};

function groupColor(group: string): string {
    if (group.startsWith('Equipamiento')) return 'bg-violet-500';
    return GROUP_COLORS[group] ?? 'bg-muted-foreground';
}

function groupBorder(group: string): string {
    if (group.startsWith('Equipamiento')) return 'border-violet-500/20 bg-violet-500/5';
    return GROUP_BORDER[group] ?? 'border-border/50 bg-muted/10';
}


export function ChestContextEditor({
    contextKey,
    config,
    equipmentCatalog,
    onChange,
    percentMode,
}: ChestContextEditorProps) {
    const metas = useMemo(
        () => getItemsForContext(contextKey, equipmentCatalog),
        [contextKey, equipmentCatalog]
    );
    const grouped = useMemo(() => groupItemsByCategory(metas), [metas]);
    const total = sumEnabledWeights(config);
    const isValid = !percentMode || Math.abs(total - 100) < 0.5;

    const barSegments = useMemo(() => {
        return Array.from(grouped.entries()).map(([group, items]) => ({
            group,
            pct: items.reduce((s, m) => {
                const item = config.items[m.key];
                return s + (item?.enabled ? Number(item.weight) || 0 : 0);
            }, 0),
        }));
    }, [grouped, config.items]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Power size={16} className={config.enabled ? 'text-emerald-400' : 'text-muted-foreground'} />
                    <div>
                        <div className="text-sm font-semibold">Canal activo</div>
                        <div className="text-xs text-muted-foreground">
                            Si está desactivado, se usa una recompensa alternativa segura
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onChange(toggleContextEnabled(config, !config.enabled))}
                    className={cn(
                        'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                        config.enabled
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                            : 'border-border text-muted-foreground'
                    )}
                >
                    {config.enabled ? 'Activado' : 'Desactivado'}
                </button>
            </div>

            <div
                className={cn(
                    'flex items-center justify-between px-4 py-3 rounded-xl border',
                    isValid ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'
                )}
            >
                <div className="flex items-center gap-2">
                    <Info size={14} className={isValid ? 'text-emerald-400' : 'text-red-400'} />
                    <span className="text-sm text-muted-foreground">
                        {percentMode
                            ? 'Suma de probabilidades activas (debe ser 100%)'
                            : 'Pesos relativos activos (se normalizan con multiplicadores de loadout)'}
                    </span>
                </div>
                <span className={cn('text-sm font-bold font-mono', isValid ? 'text-emerald-400' : 'text-red-400')}>
                    {total.toFixed(2)}
                    {percentMode ? '%' : ''}
                </span>
            </div>

            {percentMode && (
                <div className="rounded-lg overflow-hidden h-4 flex bg-muted/30 border border-border/50">
                    {barSegments.map(({ group, pct }) => {
                        if (pct <= 0) return null;
                        return (
                            <div
                                key={group}
                                className={cn(
                                    'h-full transition-all duration-300',
                                    groupColor(group)
                                )}
                                style={{ width: `${Math.max(pct, 0.5)}%` }}
                                title={`${group}: ${pct.toFixed(2)}%`}
                            />
                        );
                    })}
                </div>
            )}

            {Array.from(grouped.entries()).map(([group, items]) => {
                const groupTotal = items.reduce((s, m) => {
                    const item = config.items[m.key];
                    return s + (item?.enabled ? Number(item.weight) || 0 : 0);
                }, 0);

                return (
                    <div
                        key={group}
                        className={cn('rounded-xl border p-4 space-y-3', groupBorder(group))}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <h3 className="text-sm font-semibold">{group}</h3>
                                {group === 'Merchandising' && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 shrink-0">
                                        Solo España peninsular
                                    </span>
                                )}
                            </div>
                            <span className="text-xs font-mono text-muted-foreground shrink-0">
                                {groupTotal.toFixed(2)}
                                {percentMode ? '%' : ''} total
                            </span>
                        </div>

                        {group === 'Merchandising' && (
                            <p className="text-xs text-muted-foreground leading-relaxed border border-amber-500/20 bg-amber-500/5 rounded-lg px-3 py-2">
                                {MERCH_GEO_ADMIN_NOTE}
                            </p>
                        )}

                        {group === 'Suscripción Pro' && (
                            <p className="text-xs text-muted-foreground leading-relaxed border border-amber-500/20 bg-amber-500/5 rounded-lg px-3 py-2">
                                {PRO_1M_ADMIN_NOTE}
                            </p>
                        )}

                        <div className="hidden sm:grid grid-cols-[auto_1fr_6rem] gap-3 px-1 text-xs text-muted-foreground uppercase tracking-wide">
                            <span>Activo</span>
                            <span>Tipo de recompensa</span>
                            <span className="text-right">Probabilidad</span>
                        </div>

                        <div className="space-y-2">
                            {items.map((meta) => {
                                const item = config.items[meta.key] ?? { weight: 0, enabled: false };
                                return (
                                    <div
                                        key={meta.key}
                                        className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_6rem] gap-3 items-center"
                                    >
                                        <button
                                            type="button"
                                            title={item.enabled ? 'Desactivar' : 'Activar'}
                                            onClick={() =>
                                                onChange(toggleItemEnabled(config, meta.key, !item.enabled))
                                            }
                                            className={cn(
                                                'w-9 h-9 rounded-lg border text-xs font-bold shrink-0',
                                                item.enabled
                                                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                                    : 'border-border bg-muted/30 text-muted-foreground'
                                            )}
                                        >
                                            {item.enabled ? 'ON' : 'OFF'}
                                        </button>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">{meta.label}</div>
                                            {meta.description && (
                                                <div className="text-xs text-muted-foreground">{meta.description}</div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-end gap-1">
                                            <input
                                                type="number"
                                                min={0}
                                                max={percentMode ? 100 : undefined}
                                                step={0.01}
                                                value={item.weight}
                                                onChange={(e) => {
                                                    const val =
                                                        e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                    if (isNaN(val) || val < 0) return;
                                                    onChange(updateItemWeight(config, meta.key, val));
                                                }}
                                                className={cn(
                                                    'w-full max-w-[5.5rem] px-2 py-1.5 rounded-lg border border-border bg-background text-sm font-mono text-right',
                                                    !item.enabled && 'opacity-60'
                                                )}
                                            />
                                            <span className="text-xs text-muted-foreground w-3">
                                                {percentMode ? '%' : ''}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
